import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const fmt = (val) => {
  if (val == null) return '—';
  const n = Number(val);
  if (isNaN(n) || n === 0) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

// Round up to a clean 1-sig-fig "nice" number ($1M, $2.5M, $500K, …).
const niceCeil = (v) => {
  if (!(v > 0)) return 0;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  for (const s of [1, 2, 2.5, 5]) if (v <= s * mag) return s * mag;
  return 10 * mag;
};

// Compact currency for the "< threshold" negligible label — no trailing ".0".
const fmtCompact = (n) => {
  if (n >= 1_000_000) { const m = n / 1_000_000; return `$${Number.isInteger(m) ? m : m.toFixed(1)}M`; }
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
};

const BANDS = [
  {
    label: 'P50 · Median Scenario', key: 'p50', color: 'bg-blue-400', highlight: false,
    detail: 'Median scenario loss — in half of all simulated years, total annual losses fall at or below this figure. Occurrence probability is already built into this figure via each risk’s own TEF (Threat Event Frequency) and VULN (Vulnerability) draws — no further adjustment is needed.',
  },
  {
    label: 'P75 · Elevated', key: 'p75', color: 'bg-yellow-400', highlight: false,
    detail: 'Elevated risk scenario — 75% of AI-simulated scenarios fall below this figure. Represents a moderately adverse attack outcome with limited containment.',
  },
  {
    label: 'P90 · Stress', key: 'p90', color: 'bg-orange-500', highlight: false,
    detail: 'Stress scenario threshold — 90% of simulated scenarios fall below this value. Used for contingency planning and insurance limit sizing.',
  },
  {
    label: 'P95 · Severe', key: 'p95', color: 'bg-red-400', highlight: false,
    detail: 'Severe stress scenario — only 5% of AI simulations exceed this figure. Used for worst-case contingency planning and stress-testing financial reserves.',
  },
];

const MonteCarloSection = ({ monteCarlo, fairEAL: fairEALProp, fairEALLow, fairEALHigh, showHeroEAL = true }) => {
  const [expandedKey, setExpandedKey] = useState(null);

  if (!monteCarlo) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 text-sm text-gray-500">
        Predictive Risk Distribution unavailable — financial baseline data not yet calculated for this assessment.
      </div>
    );
  }

  // Scale the bars against the largest DISPLAYED band (P95), not P99. P99 is not shown as a row, so
  // anchoring the axis on it left the P95 "Severe" bar only partly filled (≈ P95/P99) even though it
  // is the biggest number on screen. Anchoring on P95 fills its bar (the "+" suffix already conveys
  // "and beyond") and scales P50/P75/P90 proportionally beneath it.
  const maxVal = Math.max(
    Number(monteCarlo.p95) || 0,
    Number(monteCarlo.p90) || 0,
    Number(monteCarlo.p75) || 0,
    Number(monteCarlo.p50) || 0,
    1
  );

  // A percentile can legitimately be $0 (for low-frequency risks like ransomware, the median
  // simulated year sees no loss). Render that as "< X" — where X is scale-relative (2% of the
  // smallest positive band, rounded up) so it reads as "negligible" against the numbers on screen —
  // instead of a bare "$0" that looks like missing data.
  const positiveBands = [monteCarlo.p75, monteCarlo.p90, monteCarlo.p95, monteCarlo.p99]
    .map(Number).filter(v => v > 0);
  const negligible = positiveBands.length ? niceCeil(Math.min(...positiveBands) * 0.02) : 0;
  const fmtBand = (val) => {
    if (val == null) return '—';
    const n = Number(val);
    if (isNaN(n)) return '—';
    if (n === 0) return negligible > 0 ? `< ${fmtCompact(negligible)}` : '$0';
    return fmt(n);
  };

  const deterministicLow  = fairEALLow  != null && Number(fairEALLow)  > 0 ? Number(fairEALLow)  : null;
  const deterministicHigh = fairEALHigh != null && Number(fairEALHigh) > 0 ? Number(fairEALHigh) : null;
  const deterministicPoint = fairEALProp != null && Number(fairEALProp) > 0 ? Number(fairEALProp) : null;
  // FAIR band value: prefer the Conservative–Severe range, fall back to the single-point estimate.
  const hasDeterministic = deterministicLow != null || deterministicHigh != null || deterministicPoint != null;
  const deterministicLabel = deterministicLow != null && deterministicHigh != null
    ? <>{fmt(deterministicLow)} – {fmt(deterministicHigh)}</>
    : fmt(deterministicPoint ?? deterministicHigh ?? deterministicLow);

  const toggle = (key) => setExpandedKey(prev => prev === key ? null : key);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 h-full">

      {/* ── Band 1: FAIR-Aligned (Deterministic) — separate from Monte Carlo ──
          Heading always labels this as the FAIR figure so it is never mistaken for a
          Monte Carlo number. When showHeroEAL is set, the prominent hero EAL sits in the
          heading row (right) instead of as a separate floating block. */}
      {hasDeterministic && (
        <div className="mb-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">FAIR-Aligned Expected Annual Loss</h3>
              <p className="text-xs text-gray-400 mt-0.5">Deterministic &middot; AI-informed inputs</p>
            </div>
            {showHeroEAL && (
              <div className="text-right flex-shrink-0">
                <div className="text-xs text-gray-400 mb-0.5">Estimated Annual Exposure</div>
                <div className="text-2xl font-bold text-emerald-700 tabular-nums leading-tight">
                  {fairEALLow != null && fairEALHigh != null
                    ? <>{fmt(fairEALLow)} – {fmt(fairEALHigh)}</>
                    : fmt(fairEALProp ?? deterministicPoint)}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 rounded bg-gray-50 border border-gray-200 px-3 py-2 mt-2">
            <span className="w-24 text-xs shrink-0 font-semibold text-gray-700">
              Conservative – Severe
            </span>
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div className="bg-gray-500 h-1.5 rounded-full" style={{ width: '100%' }} />
            </div>
            <span className="text-right text-sm tabular-nums font-bold text-gray-800 whitespace-nowrap">
              {deterministicLabel}+
            </span>
          </div>
        </div>
      )}

      {/* Divider between the two bands */}
      {hasDeterministic && (
        <div className="border-t border-gray-200 my-3" />
      )}

      {/* ── Band 2: Monte Carlo Scenario Distribution ── */}
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900">Monte Carlo Scenario Distribution</h3>
        {(monteCarlo.iterationCount != null || monteCarlo.riskCount != null) && (
          <p className="text-xs text-gray-400 mt-0.5">
            {monteCarlo.iterationCount != null && <>{(monteCarlo.iterationCount).toLocaleString()} simulated scenarios</>}
            {monteCarlo.iterationCount != null && monteCarlo.riskCount != null && <>&nbsp;&middot;&nbsp;</>}
            {monteCarlo.riskCount != null && <>{monteCarlo.risksWithCompleteFinancials ?? 0} of {monteCarlo.riskCount} risks</>}
            {monteCarlo.mean != null && Number(monteCarlo.mean) > 0 && (
              <>&nbsp;&middot;&nbsp;Mean (expected) <span className="font-semibold text-gray-600">{fmt(monteCarlo.mean)}</span></>
            )}
          </p>
        )}
      </div>

      {/* Partial data badge — no schema version */}
      {monteCarlo.isPartialResult && (
        <div className="mb-2">
          <span className="text-xs bg-yellow-50 text-yellow-800 border border-yellow-300 rounded px-2 py-0.5">
            Partial — {monteCarlo.risksWithCompleteFinancials} of {monteCarlo.riskCount} risks included
          </span>
        </div>
      )}

      {/* Percentile bars — click to expand detail */}
      <div className="space-y-1">
        {BANDS.filter(({ key }) => monteCarlo[key] != null).map(({ label, key, color, highlight, detail }) => {
          const value = monteCarlo[key];
          const pct   = Math.min(100, (Number(value) / maxVal) * 100);
          const open  = expandedKey === key;

          return (
            <div key={key}>
              <button
                onClick={() => toggle(key)}
                className={`w-full flex items-center gap-3 rounded text-left transition-colors py-0.5
                  ${open ? 'ring-1 ring-inset ring-gray-200 rounded' : 'hover:bg-gray-50'}`}
              >
                <span className="w-24 text-xs shrink-0 text-gray-500">
                  {label}
                </span>
                <div className="flex-1 bg-gray-100 rounded-full h-2">
                  <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <span className="w-20 text-right text-sm tabular-nums font-medium text-gray-800">
                  {fmtBand(value)}{key === 'p95' ? '+' : ''}
                </span>
                {open
                  ? <ChevronUp className="w-3 h-3 text-gray-400 shrink-0" />
                  : <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />}
              </button>

              {/* Expanded detail */}
              {open && (
                <div className="mt-1 mb-1 ml-2 pl-3 border-l-2 border-gray-200 text-xs text-gray-500 space-y-1.5">
                  <p>{detail}</p>
                  {key === 'p90' && (
                    <div className={`grid ${monteCarlo.maxObserved != null ? 'grid-cols-3' : 'grid-cols-2'} gap-2 pt-1 text-center`}>
                      <div>
                        <div className="text-gray-400">Mean</div>
                        <div className="font-semibold text-gray-700">{fmt(monteCarlo.mean)}</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Std Dev</div>
                        <div className="font-semibold text-gray-700">{fmt(monteCarlo.standardDeviation)}</div>
                      </div>
                      {monteCarlo.maxObserved != null && (
                        <div>
                          <div className="text-gray-400">Worst</div>
                          <div className="font-semibold text-red-600">{fmt(monteCarlo.maxObserved)}</div>
                        </div>
                      )}
                    </div>
                  )}
                  {key === 'p50' && hasDeterministic && (
                    <p className="text-gray-400">
                      FAIR-aligned deterministic estimate: {deterministicLabel} — Monte Carlo scenario bands typically run higher due to simulated attack multipliers and scenario variance.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Model interpretation note */}
      <p className="mt-3 text-[10px] text-gray-400 leading-relaxed border-t border-gray-100 pt-2">
        <span className="font-medium text-gray-500">How to read these numbers:</span>{" "}
        Scenario bands (P50–P95) already reflect annual occurrence probability — each simulated year draws attack
        attempts from a Poisson(TEF) distribution and applies a Bernoulli(VULN) success check per attempt (mc-v3.2),
        so no further probability adjustment is needed. A "&lt; X" median means most simulated years see no loss —
        expected annual loss is carried by the tail, so the Mean (not the median) is the budgeting figure. The FAIR-Aligned EAL is the FAIR-Aligned point estimate from
        the same FAIR-Aligned factor inputs. Use the scenario bands for insurance limits, contingency reserves, and stress
        testing; use the FAIR-Aligned EAL as the single-number annual risk budget baseline.
      </p>


      {/* Partial-data disclaimer */}
      {monteCarlo.isPartialResult && (
        <p className="mt-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
          {(monteCarlo.riskCount ?? 0) - (monteCarlo.risksWithCompleteFinancials ?? 0)} risk(s) excluded — no complete financial baseline. Results are directional.
        </p>
      )}
    </div>
  );
};

export default MonteCarloSection;
